// Central role/permission definitions for the platform.
//
// The matrix below mirrors the permission table provided by the product
// owner (Role x Admin/Operator/Viewer/Driver). Rows marked with "*" in the
// original table (e.g. "เห็นบริษัทที่ถูก Assign", "จัดการ Plan (สาขา)" for
// Operator) are scoped to the companies/plans the user is actually assigned
// to — that scoping is expected to be enforced server-side (the API only
// ever returns records the token is allowed to see); the flags here just
// describe whether the *feature* is available to the role at all.
//
// Permission keys:
//   seeAllCompanies            - เห็นทุกบริษัท
//   seeAssignedCompanies       - เห็นบริษัทที่ถูก Assign
//   createDeleteOwner          - สร้าง/ลบ Owner
//   manageOwner                - จัดการ Owner
//   createDeletePlatformAdmin  - สร้าง/ลบ PlatformAdmin
//   managePlatformAdmin        - จัดการ PlatformAdmin
//   createDeleteCompany        - สร้าง/ลบบริษัท
//   manageAssignedCompany      - จัดการบริษัทที่ Assign
//   createDeleteGroup          - สร้าง/ลบ Group
//   createDeleteAdmin          - สร้าง/ลบ Admin
//   createDeleteOperatorViewer - สร้าง/ลบ Operator, Viewer
//   createDeleteOperatorDriver - สร้าง/ลบ Operator, Driver (Operator role's
//                                 own limited version of user management —
//                                 they may add accounts, but only as
//                                 Operator or Driver, never Admin/Viewer/Owner)
//   manageGroup                - จัดการ Group
//   managePlan                 - จัดการ Plan (สาขา) — access the section and
//                                 manage cars/assets/devices within a plan
//   managePlanRecord           - สร้าง/แก้ไข/ลบ Plan (สาขา) เอง — narrower
//                                 than managePlan: Operator can work inside a
//                                 plan but must not create/edit/delete the
//                                 plan (location/vehicle group) record itself
//   manageDevice                - จัดการ Device
//   viewDataInAssignedCompany  - ดูข้อมูลในบริษัทที่ Assign
//   viewData                   - ดู Data
//   sendCommand                - ส่ง Command
//   exportReport               - Export Report

const PERMISSION_KEYS = [
  'seeAllCompanies',
  'seeAssignedCompanies',
  'createDeleteOwner',
  'manageOwner',
  'createDeletePlatformAdmin',
  'managePlatformAdmin',
  'createDeleteCompany',
  'manageAssignedCompany',
  'createDeleteGroup',
  'createDeleteAdmin',
  'createDeleteOperatorViewer',
  'createDeleteOperatorDriver',
  'manageGroup',
  'managePlan',
  'managePlanRecord',
  'manageDevice',
  'viewDataInAssignedCompany',
  'viewData',
  'sendCommand',
  'exportReport',
]

// company_role permission matrix, taken directly from the provided table.
export const PERMISSION_MATRIX = {
  admin: {
    seeAllCompanies: false,
    seeAssignedCompanies: true,
    createDeleteOwner: false,
    manageOwner: false,
    createDeletePlatformAdmin: false,
    managePlatformAdmin: false,
    createDeleteCompany: false,
    manageAssignedCompany: false,
    createDeleteGroup: false,
    createDeleteAdmin: false,
    createDeleteOperatorViewer: true,
    createDeleteOperatorDriver: false,
    manageGroup: true,
    managePlan: true,
    managePlanRecord: true,
    manageDevice: true,
    viewDataInAssignedCompany: true,
    viewData: true,
    sendCommand: true,
    exportReport: true,
  },
  // Company Owner is treated the same as Admin in the UI — the sidebar and
  // every other permission-gated feature should look identical for both
  // roles.
  company_owner: {
    seeAllCompanies: false,
    seeAssignedCompanies: true,
    createDeleteOwner: false,
    manageOwner: false,
    createDeletePlatformAdmin: false,
    managePlatformAdmin: false,
    createDeleteCompany: false,
    manageAssignedCompany: false,
    createDeleteGroup: false,
    createDeleteAdmin: false,
    createDeleteOperatorViewer: true,
    createDeleteOperatorDriver: false,
    manageGroup: true,
    managePlan: true,
    managePlanRecord: true,
    manageDevice: true,
    viewDataInAssignedCompany: true,
    viewData: true,
    sendCommand: true,
    exportReport: true,
  },
  operator: {
    seeAllCompanies: false,
    seeAssignedCompanies: true,
    createDeleteOwner: false,
    manageOwner: false,
    createDeletePlatformAdmin: false,
    managePlatformAdmin: false,
    createDeleteCompany: false,
    manageAssignedCompany: false,
    createDeleteGroup: false,
    createDeleteAdmin: false,
    createDeleteOperatorViewer: false,
    createDeleteOperatorDriver: true,
    manageGroup: false,
    managePlan: true,
    // Operator can access a plan and manage the cars/assets/devices inside
    // it, but must not create/edit/delete the plan (location/vehicle group)
    // record itself.
    managePlanRecord: false,
    manageDevice: true,
    viewDataInAssignedCompany: true,
    viewData: true,
    sendCommand: true,
    exportReport: true,
  },
  viewer: {
    seeAllCompanies: false,
    seeAssignedCompanies: true,
    createDeleteOwner: false,
    manageOwner: false,
    createDeletePlatformAdmin: false,
    managePlatformAdmin: false,
    createDeleteCompany: false,
    manageAssignedCompany: false,
    createDeleteGroup: false,
    createDeleteAdmin: false,
    createDeleteOperatorViewer: false,
    createDeleteOperatorDriver: false,
    manageGroup: false,
    managePlan: false,
    managePlanRecord: false,
    manageDevice: false,
    viewDataInAssignedCompany: true,
    viewData: true,
    sendCommand: false,
    exportReport: false,
  },
  driver: {
    seeAllCompanies: false,
    seeAssignedCompanies: false,
    createDeleteOwner: false,
    manageOwner: false,
    createDeletePlatformAdmin: false,
    managePlatformAdmin: false,
    createDeleteCompany: false,
    manageAssignedCompany: false,
    createDeleteGroup: false,
    createDeleteAdmin: false,
    createDeleteOperatorViewer: false,
    createDeleteOperatorDriver: false,
    manageGroup: false,
    managePlan: false,
    managePlanRecord: false,
    manageDevice: false,
    viewDataInAssignedCompany: false,
    viewData: true,
    sendCommand: false,
    exportReport: false,
  },
}

// Every permission granted — used for the "platform operator" fallback
// (see getEffectiveRole) which is meant to see/do everything on the platform.
const FULL_ACCESS = PERMISSION_KEYS.reduce((acc, key) => {
  acc[key] = true
  return acc
}, {})

// No permissions at all — safe fallback for a role we don't recognise.
const NO_ACCESS = PERMISSION_KEYS.reduce((acc, key) => {
  acc[key] = false
  return acc
}, {})

const FULL_ACCESS_ROLE = '__platform_full_access__'

function normalize(role) {
  return (role || '').toString().trim().toLowerCase()
}

/**
 * Resolves the role that should be used to look up permissions.
 *
 * Priority:
 *  1. company_role (from localStorage) — the role a user has within their
 *     assigned company. This is the primary source of truth, and is looked
 *     up against PERMISSION_MATRIX (admin/operator/viewer/driver).
 *  2. globalRole — used only when there is no company_role at all. A user
 *     with no company_role isn't scoped to a single company, so any
 *     globalRole (owner, operator, admin, ...) is treated as platform-level
 *     and gets full access to everything on the platform. If neither
 *     company_role nor globalRole is set (e.g. not logged in), no access
 *     is granted.
 */
export function getEffectiveRole() {
  const companyRole = normalize(localStorage.getItem('company_role'))
  if (companyRole) return companyRole

  const globalRole = normalize(localStorage.getItem('globalRole'))
  if (globalRole) return FULL_ACCESS_ROLE
  return ''
}

export function getPermissions() {
  const role = getEffectiveRole()
  if (role === FULL_ACCESS_ROLE) return FULL_ACCESS
  return PERMISSION_MATRIX[role] || NO_ACCESS
}

export function can(permissionKey) {
  return !!getPermissions()[permissionKey]
}

export function hasFullAccess() {
  return getEffectiveRole() === FULL_ACCESS_ROLE
}

// Human-friendly label for the role currently in effect (for UI display).
export function getRoleLabel() {
  const companyRole = localStorage.getItem('company_role')
  if (companyRole) return companyRole
  const globalRole = localStorage.getItem('globalRole')
  return globalRole ? `${globalRole} (แพลตฟอร์ม)` : ''
}

export default {
  PERMISSION_MATRIX,
  getEffectiveRole,
  getPermissions,
  can,
  hasFullAccess,
  getRoleLabel,
}
